import { useState, useCallback, useRef } from "react";
import api from "../../../config/axios";
import { logger } from "../../../utils/logger";
import { onlyDigits } from "../../clients/utils/clientIdentityHelpers";

export interface Cliente {
  id: number;
  nombre: string;
  apellido: string;
  dni: string;
  telefono: string;
  email: string;
  localidad: string;
  es_empresa: boolean;
  origen_reserva: string;
}

/** Coincidencia por identidad fiscal independiente de guiones/formato en BD o input. */
export async function findClienteByDocumentDigits(
  dniInput: string
): Promise<Cliente | null> {
  const digits = onlyDigits(dniInput || "");
  if (!digits) {
    return null;
  }

  try {
    const response = await api.get<Cliente[]>(
      `/clientes/sugerencias?query=${encodeURIComponent(digits)}`
    );
    return (
      response.data.find((c) => onlyDigits(c.dni) === digits) || null
    );
  } catch {
    return null;
  }
}

export const useClienteSearch = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sugerencias, setSugerencias] = useState<Cliente[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const buscarClientes = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSugerencias([]);
      return;
    }

    setLoading(true);
    setError(null);

    // Cancelar la petición anterior si aún está en vuelo
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const response = await api.get<Cliente[]>(
        `/clientes/sugerencias?query=${encodeURIComponent(query)}`,
        { signal }
      );
      setSugerencias(response.data);
    } catch (err: unknown) {
      // Ignorar el error si fue causado por nuestra propia cancelación
      const cancelError =
        typeof err === "object" && err !== null
          ? (err as { name?: string; code?: string })
          : {};
      if (
        cancelError.name === "CanceledError" ||
        cancelError.code === "ERR_CANCELED"
      ) {
        logger.info("Petición de clientes cancelada por nueva entrada");
        return;
      }
      logger.error("Error al buscar clientes:", err);
      setError("Error al buscar clientes. Por favor, inténtalo de nuevo.");
      setSugerencias([]);
    } finally {
      // Solo quitar el loading si esta es la última petición (no cancelada)
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  const validarDNIExistente = useCallback(
    async (dni: string): Promise<Cliente | null> => findClienteByDocumentDigits(dni),
    []
  );

  return {
    loading,
    error,
    sugerencias,
    buscarClientes,
    validarDNIExistente,
  };
};
